import { useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { useTooltip } from '@frontend/design-system';
import { SchemaForm } from './schema';

/**
 * Custom hook for notification validation and tooltip functionality
 * Used in voicemail box forms to validate that email/SMS contacts exist when notifications are enabled
 */
export const useNotificationValidation = (form: SchemaForm) => {
  const { t } = useTranslation('phone');

  // Get form field props for notification validation
  const notificationEnabledField = form.getFieldProps('notificationsEnabled');
  const emailField = form.getFieldProps('notificationEmail');
  const smsField = form.getFieldProps('notificationSMS');

  // Check if notifications are enabled but no email/SMS entries exist
  const notificationsEnabledWithoutContacts = useMemo(() => {
    return (
      notificationEnabledField.value &&
      (!emailField.value || emailField.value.length === 0) &&
      (!smsField.value || smsField.value.length === 0)
    );
  }, [notificationEnabledField.value, emailField.value, smsField.value]);

  // Setup tooltip for save button
  const {
    Tooltip: SaveButtonTooltip,
    triggerProps: saveButtonTriggerProps,
    tooltipProps: saveButtonTooltipProps,
  } = useTooltip({ placement: 'top-start' });

  // Create memoized tooltip element to avoid recreating on every render
  const NotificationTooltip = useMemo(() => {
    return (
      <SaveButtonTooltip {...saveButtonTooltipProps}>
        {t('You must add an email or SMS number in order to enable notifications')}
      </SaveButtonTooltip>
    );
  }, [SaveButtonTooltip, saveButtonTooltipProps, t]);

  return {
    notificationsEnabledWithoutContacts,
    saveButtonTriggerProps,
    NotificationTooltip,
  };
};
